import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Tag, Track } from '@app/core/models/Track';
import {
  RemoveTagsFromMultipleTrackFailure,
  RemoveTagsFromMultipleTrackRequest,
  RemoveTagsFromMultipleTrackSuccess,
} from '@app/library/states/radio-tags.actions';
import { RadioTagsState } from '@app/library/states/radio-tags.state';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { flatten } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

interface DataInput {
  tracks: Track[];
}

@Component({
  selector: 'rk-remove-tags-modal',
  templateUrl: './remove-tags-modal.component.html',
  styleUrls: ['./remove-tags-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RemoveTagsModalComponent implements OnInit {
  isLoading$: Observable<boolean>;

  formTags: UntypedFormGroup;

  @Select(RadioTagsState.tagsFormatted)
  tags$: Observable<SugestionInput[]>;

  tagsOfTracks$: Observable<SugestionInput[]>;

  subscription = new Subscription();

  constructor(
    public readonly dialogRef: MatDialogRef<RemoveTagsModalComponent>,
    private readonly uiStore: UiStoreHelperService,
    private readonly fb: UntypedFormBuilder,
    private readonly actions$: Actions,
    @Inject(MAT_DIALOG_DATA) public dataIn: DataInput,
  ) {}

  ngOnInit() {
    this.isLoading$ = this.uiStore.getLoadingState(
      [RemoveTagsFromMultipleTrackRequest],
      [RemoveTagsFromMultipleTrackSuccess, RemoveTagsFromMultipleTrackFailure],
    );

    // Restrict to tags of specified tracks
    const tagIdsOfTracks = new Set(
      flatten(this.dataIn.tracks.map(track => track.tags))
        .filter(tags => tags)
        .map(tag => tag.id),
    );
    this.tagsOfTracks$ = this.tags$.pipe(
      map(tags => tags.filter(tag => tagIdsOfTracks.has(tag.id))),
    );

    this.formTags = this.fb.group({
      tags: [[]],
    });

    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(RemoveTagsFromMultipleTrackSuccess))
        .subscribe(() => this.dialogRef.close()),
    );
  }

  @Dispatch()
  onSubmit() {
    return new RemoveTagsFromMultipleTrackRequest(
      this.dataIn.tracks.map(t => t.id),
      this.formTags.get('tags').value.map((t: Tag) => t.id),
    );
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
